import {Component, EventEmitter, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {ToastService} from "../../services/Toast.service";
import {StateService} from "../../services/StateService";
import {ActivatedRoute, Router} from "@angular/router";
import {LogoService} from "../../services/LogoService";
import {LoadingService} from "../../services/LoadingService";
import {TOAST_TYPE} from "../../constants/Tpiq.constants";
import {TaxPlanningService} from "../../tpiq-services/tax-planning.service";
import {State} from "../../models/State";
import {ExportService} from "../../services/Export.service";
import {PageTitleService} from "../../services/PageTitle";
import {Session} from "../../services/Session";
import {environment} from "../../../environments/environment";
import {TpiqSelectDropdownComponent} from "../../input-search/tpiq-select-dropdown/tpiq-select-dropdown.component";
import {RoiPageComponent} from "../../sidebar-components/roi-page/roi-page.component";

declare const jQuery: any;
declare const moment: any;
declare const _: any;

@Component({
  selector: 'app-customer-tax-planning',
  standalone: true,
  imports: [CommonModule, FormsModule, TpiqSelectDropdownComponent, RoiPageComponent],
  templateUrl: './customer-tax-planning.component.html',
  styleUrl: './customer-tax-planning.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class CustomerTaxPlanningComponent implements OnInit, OnDestroy {
  org: string;
  user: any = {};
  taxPlanId: string;
  roiData: any;
  scenarios: Array<any> = [];
  clientUserId: string;
  hasROIData: boolean;
  views: Array<any> = [{name: 'Category'}, {name: 'Related Party'}, {name: 'Strategy'}, ];
  currentView: string;
  yearFilter: any;
  filterYears: Array<any> = [];
  orgLogo: string;
  currentYear: any;
  orgName: string;
  hasScenarioData = false;
  disclaimer: string;

  destroy$: EventEmitter<void> = new EventEmitter<void>();
  filteredViews: Array<any> = [];
  currentEnvironment: any;
  displayLogo: boolean = true;

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private logoService: LogoService,
    private exportService: ExportService,
    private loadingService: LoadingService,
    private toastService: ToastService,
    private taxPlanningService: TaxPlanningService,
    private pageTitleService: PageTitleService,
    private stateService: StateService
  ) {}

  ngOnInit(): void {
    this.org = Session.getOrg();
    this.user = Session.getUser('delegatedFrom');
    this.currentEnvironment = environment;
    this.orgName = this.user.org || '';
    this.clientUserId = this.user.id;
    this.orgLogo = this.logoService.getLoginLogoUrl();
    this.getDefaultPlanInfo();
    this.pageTitleService.setPageTitle('Tax Planning & ROI');
  }

  getDefaultPlanInfo() {
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getClientDefaultPlansInfo(this.org, this.user.id, this.clientUserId)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.filterYears = response.publishedYears;
        if (this.filterYears.length > 0) {
          const yearObject = _.maxBy(this.filterYears, function(year) { return year.id; });
          const prevState = this.stateService.getPrevState();
          if (prevState && prevState.data) {
            this.yearFilter = (prevState.data.year) ? prevState.data.year : yearObject.id;
          } else {
            this.yearFilter = yearObject.id;
          }
        }
        this.getTaxPlans();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed To Access Plan Info');
      });

  }

  ngOnDestroy(): void {
  }

  setView(view){
    this.currentView = view.name;
    this.getTaxPlanningSummary(this.currentView, this.yearFilter);
  }

  setScenario(scenario){
    this.taxPlanId = scenario.id;
    this.filteredViews = this.getFilteredViews(scenario.views);
    this.currentView = (this.filteredViews.length > 0) ? this.filteredViews[0].name : '';
    this.yearFilter = (this.yearFilter) ? this.yearFilter : '';
    this.getTaxPlanningSummary(this.currentView, this.yearFilter);
  }

  getTaxPlans(){
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getClientPlans(this.org, this.user.id, this.clientUserId, this.yearFilter, 'publish', true)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.scenarios = response || [];
        this.hasScenarioData = true;
        if (this.scenarios.length > 0){
          const prevState = this.stateService.getPrevState();
          if (prevState && prevState.data) {
            this.stateService.pop();
            this.taxPlanId = (prevState.data.taxPlan) ? prevState.data.taxPlan : this.scenarios[0].id;
            const obj = _.find(this.scenarios, {id: this.taxPlanId});
            if (obj) {
              this.filteredViews = this.getFilteredViews(obj.views);
            }
            this.currentView = (prevState.data.view) ? prevState.data.view : this.filteredViews[0].name;
          } else {
            this.filteredViews = this.getFilteredViews(this.scenarios[0].views);
            this.taxPlanId = this.scenarios[0].id;
            this.currentView = (this.filteredViews.length > 0) ? this.filteredViews[0].name : '';
          }
          this.getTaxPlanningSummary(this.currentView, this.yearFilter);
        }
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
      });
  }

  getTaxPlanningSummary(view, filter){
    this.loadingService.triggerLoadingEvent(true);
    this.taxPlanningService.getTaxPlanningSummary(this.org, this.user.id, this.taxPlanId, view, filter)
      .subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.roiData = response;
        this.hasROIData = true;
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
      });
  }

  filterROIPage($event){
    this.yearFilter = $event.id;
    this.getTaxPlans();
  }

  exportToExcel(page){
    if (page == 'roi_page') {
      let urlString = (this.currentEnvironment.production) ? 'https://services.taxplaniq.com' : 'https://dev-services.taxplaniq.com';
      const data: any = {};
      data.url = urlString + '/TaxPlanningServices/orgs/' + this.org + '/users/' + this.user.id + '/clientstrategy/' + this.taxPlanId + '/summary?groupby=' + this.currentView + '&filter=' + this.yearFilter;
      data.fileName = `Tax Planning - ${this.user.first_name} ${this.user.last_name}.xls`;
      data.reportName = 'ROI';
      data.org = this.org;
      data.orgName = this.orgName;
      data.orgLogo = this.orgLogo;
      data.taxYear = this.yearFilter;
      this.loadingService.triggerLoadingEvent(true);
      this.taxPlanningService.exportROIExcel(this.org, this.user.id, data)
        .subscribe(response => {
          this.loadingService.triggerLoadingEvent(false);
          const blob = new Blob([response], {type: 'application/vnd.ms-excel'});
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `Tax Planning - ${this.user.first_name} ${this.user.last_name}.xls`;
          link.click();
        }, error => {
          this.loadingService.triggerLoadingEvent(false);
          this.toastService.pop(TOAST_TYPE.error, 'Failed To Export Into Excel');
        });
    }
  }

  exportToPDF(page){
    const pdfReq = this.getPDFRequestObject();
    const url = '/TaxPlanningServices/orgs/' + this.org + '/users/' + this.user.id + '/export/pdf';
    this.loadingService.triggerLoadingEvent(true);
    this.exportService.exportReportIntoFile(url, pdfReq)
      .subscribe(data => {
        this.loadingService.triggerLoadingEvent(false);
        const blob = new Blob([data], {type: 'application/pdf'});
        const link = jQuery('<a></a>');
        link[0].href = URL.createObjectURL(blob);
        link[0].download = `Tax Planning - ${this.user.first_name} ${this.user.last_name}.pdf`;
        link[0].click();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.error, 'Failed To Export Into PDF');
      });
  }

  getPDFRequestObject(){
    let styleString = '';
    const styleHtml = jQuery('style').clone();
    if (styleHtml && styleHtml.length >= 2){
      for (let i = 1; i < styleHtml.length; i++){
        styleString += styleHtml[i].outerHTML;
      }
    }
    const img = `<img width="130%" src="${this.orgLogo}"/>`;
    this.loadingService.triggerLoadingEvent(true);
    let html = jQuery('<div>').append(styleString).append(jQuery('#numeric').clone()).html();
    html = html.replace('````', img);
    const pdfReq = {
      version : '1.1',
      genericReport: {
        payload: html,
        footer: moment(new Date()).format('MMMM DD, YYYY HH:mm a')
      }
    };
    return pdfReq;
  }

  getFilteredViews(viewsData) {
    const base = this;
    const data = [];
    _.each(viewsData, function(view){
      const viewObj: any = _.find(base.views, {name: view});
      if (viewObj) {
        data.push(viewObj);
      }
    });
    return data;
  }

  navigateToStrategyOverview(taxSave) {
    const data = {
      year: this.yearFilter,
      view: this.currentView,
      taxPlan: this.taxPlanId,
    };
    this.stateService.addState(new State('TP_ROI_PAGE', this._router.url, data, null));
    this._router.navigate(['tax-planning', 'strategy-overview', taxSave.id]);
  }

}
